import { HeroIcon } from '@components/HeroIcon'
import { ILesson } from '@dtos/Course'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useContentsModalStore } from '@stores/useContentsModalStore'
import { useLessonTabStore } from '@stores/useLessonTabStore'
import { LessonTypeTranslation } from '@utils/lessonMediaType'
import { IVideoThumbMedia, getVideoThumb } from '@utils/videoThumbs'
import { useActiveCourseContentStore } from '@stores/useActiveCourseContentStore'
import { router, useLocalSearchParams } from 'expo-router'
import { useHasFeature } from '@hooks/useThemeParams'
import { MEMBERS_PAGE_COURSE_CONTENTS } from '@constants/features'
import { heroThemeCourseContentPath } from '@utils/coursePaths'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useEffect, useMemo, useRef, useCallback } from 'react'
import { View, Text, XStack, YStack, Stack, isWeb } from 'tamagui'
import { useScrollBarStore } from '@stores/useScrollBarStore'
import { mediaDuration } from '@utils/mediaDurationFormatter'
import { CoverImage } from '@components/CoverImage'

function LessonStatusSection({
  icon,
  text,
  iconColor,
}: {
  icon: IconProp
  text: string
  iconColor: string
}) {
  return (
    <>
      <HeroIcon icon={icon} size={14} color={iconColor} />
      <Text
        color="$text-primary"
        fontFamily="$mulishSemiBold"
        fontWeight="600"
        fontSize="$font-size-xxxs"
      >
        {text}
      </Text>
    </>
  )
}

interface ILessonHorizontalCardProps {
  dataLesson: ILesson
  isAvailable: boolean
  availableLabel: string
  hasNoContent?: boolean
  courseContentsId: number
  moduleId: number
  media: IVideoThumbMedia
  isCompleted?: boolean
  /** Ditado pelo servidor (`Enrollment.blockedCourseContentIds`). */
  isBlockedByLinearProgress?: boolean
}

export function LessonHorizontalCard({
  dataLesson,
  isAvailable,
  availableLabel,
  hasNoContent,
  courseContentsId,
  moduleId,
  media,
  isCompleted,
  isBlockedByLinearProgress = false,
}: ILessonHorizontalCardProps) {
  const lessonPositionRef = useRef(0)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { isOpen, setPosition } = useContentsModalStore()
  const { scrollTo } = useScrollBarStore()
  const isSelected = useActiveCourseContentStore(
    (state) => String(state.activeLessonId) === String(dataLesson.id),
  )
  const setActiveLessonId = useActiveCourseContentStore(
    (state) => state.setActiveLessonId,
  )
  const setCurrentTab = useLessonTabStore((state) => state.setCurrentTab)
  const hasCourseContentsPage = useHasFeature(MEMBERS_PAGE_COURSE_CONTENTS)
  const { isPreview } = useAdminPreview()
  const { enrollmentId, courseId } = useLocalSearchParams()
  const displayLinearRequirementText =
    !isAvailable && !isCompleted && isBlockedByLinearProgress
  const selectedBackground = isSelected ? '$bg-surface-raised' : '$bg-surface'
  const selectedBorderColor = isSelected ? '$border-default' : '$bg-surface'
  const lessonThumbnail = getVideoThumb(media)
  const isYoutubeOrVimeo = ['Youtube', 'Vimeo'].includes(media.host || '')
  const displayThumbnail = media.coverImage || isYoutubeOrVimeo
  const lessonTitle = hasNoContent
    ? 'Em breve esse conteúdo será adicionado'
    : dataLesson.title
  const lessonIconColor = hasNoContent ? '$icon-primary' : '$accent'
  const lessonIcon = hasNoContent
    ? ['fal', 'hourglass']
    : LessonTypeTranslation(
        dataLesson.activity ? 'Quiz' : dataLesson.media?.type || 'Text',
      )

  const handleScroll = useCallback(
    (y: number) => {
      if (y <= 0) return

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      scrollTimeoutRef.current = setTimeout(() => {
        scrollTo('module-scroll', y)
      }, 300)
    },
    [scrollTo],
  )

  useEffect(() => {
    if (isOpen && isSelected && lessonPositionRef.current > 0) {
      handleScroll(lessonPositionRef.current)
    }
  }, [isOpen, isSelected, handleScroll])

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  const renderLessonStatus = useMemo(() => {
    if (isAvailable && dataLesson.media?.duration && !isCompleted) {
      return (
        <XStack
          paddingHorizontal={8}
          paddingVertical={4}
          backgroundColor="$bg-surface-strong"
          borderRadius="$border-radius-sm"
        >
          <Text
            ff="$mulishSemiBold"
            fontSize="$font-size-xxxs"
            color="$text-primary"
            fontWeight="600"
          >
            {mediaDuration(dataLesson.media.duration)}
          </Text>
        </XStack>
      )
    }

    if (isCompleted) {
      return (
        <LessonStatusSection
          icon={['fas', 'check-circle']}
          text="Item concluído"
          iconColor="$progress-complete"
        />
      )
    }

    if (availableLabel) {
      return (
        <LessonStatusSection
          icon={['fal', 'calendar-alt']}
          text={availableLabel}
          iconColor="$text-muted"
        />
      )
    }
    if (displayLinearRequirementText) {
      return (
        <LessonStatusSection
          icon={['fal', 'exclamation-circle']}
          text={'Conclua a aula anterior'}
          iconColor="$icon-primary"
        />
      )
    }
    return null
  }, [
    isAvailable,
    dataLesson.media?.duration,
    isCompleted,
    availableLabel,
    displayLinearRequirementText,
  ])

  const handlePress = useCallback(() => {
    if (!isAvailable || hasNoContent) return
    // Escolas sem a feature members_page_course_contents não têm a página de
    // consumo no SPA de membros — navegar via router deixaria a URL 404 no
    // refresh. Nesse caso, redireciona (full load) para a página legada do
    // hero_theme (URL sem /online_course).
    if (isWeb && !isPreview && !hasCourseContentsPage) {
      window.location.href = heroThemeCourseContentPath({
        enrollmentId,
        courseId,
        courseContentId: courseContentsId,
      })
      return
    }
    router.setParams({
      courseContentsId: courseContentsId.toString(),
      moduleId: moduleId.toString(),
    })
    setActiveLessonId(dataLesson.id)
    setPosition(1)
    setCurrentTab('description')
  }, [
    isAvailable,
    courseContentsId,
    moduleId,
    setActiveLessonId,
    hasNoContent,
    dataLesson.id,
    setPosition,
    setCurrentTab,
    hasCourseContentsPage,
    enrollmentId,
    courseId,
    isPreview,
  ])

  const handleLayout = useCallback(
    (event: { nativeEvent: { layout: { y: number } } }) => {
      const y = event.nativeEvent.layout.y
      if (y > 0 && isSelected) {
        lessonPositionRef.current = y
      }
    },
    [isSelected],
  )

  return (
    <View
      backgroundColor={selectedBackground}
      br="$border-radius-md"
      borderWidth={1}
      testID="lesson-horizontal-card"
      borderColor={selectedBorderColor}
      marginBottom="$spacing-inset-xs"
      cursor={isAvailable ? 'pointer' : 'default'}
      position="relative"
      onPress={handlePress}
      {...(!isWeb && {
        pressStyle: { opacity: 0.7, scale: 0.98 },
        animation: 'quick',
      })}
      overflow="visible"
      hoverStyle={{ backgroundColor: '$bg-surface-raised' }}
      marginLeft={16}
      onLayout={handleLayout}
    >
      {isSelected && (
        <Stack
          position="absolute"
          left={0}
          top="50%"
          zIndex={999}
          overflow="visible"
          style={{ overflow: 'visible' }}
          pointerEvents="none"
          transform={[{ translateX: -10 }, { translateY: -9 }]}
        >
          <HeroIcon icon={['fas', 'play']} size={16} color="$accent" />
        </Stack>
      )}
      <XStack
        gap="$spacing-inset-nano"
        padding="$spacing-inset-nano"
        alignItems="center"
      >
        <View>
          {!isAvailable && (
            <Stack
              bg="$bg-locked"
              borderRadius="$border-radius-pill"
              position="absolute"
              zIndex={100}
              padding={8}
              right="50%"
              top="50%"
              transform={[{ translateX: 15 }, { translateY: -16 }]}
            >
              <HeroIcon
                icon={['fal', 'lock-keyhole']}
                color="$text-on-locked"
                size={16}
              />
            </Stack>
          )}
          {displayThumbnail ? (
            <CoverImage
              testID="lesson-thumbnail"
              uri={lessonThumbnail || undefined}
              width={82}
              height={47}
              borderRadius={4}
              grayscale={!isAvailable}
              alt={dataLesson.title}
            />
          ) : (
            <XStack
              br="$border-radius-sm"
              bg="$bg-surface-sunken"
              testID="lesson-icon"
              alignItems="center"
              justifyContent="center"
              height={47}
              width={82}
            >
              <HeroIcon
                icon={lessonIcon as IconProp}
                color={lessonIconColor}
                size={16}
              />
            </XStack>
          )}
        </View>
        <YStack
          justifyContent="space-between"
          gap="$spacing-inset-quarck"
          flex={1}
          alignSelf="stretch"
        >
          <Text
            fontFamily="$mulishRegular"
            color="$text-primary"
            fontSize="$font-size-xxs"
            numberOfLines={3}
            width="90%"
          >
            {lessonTitle}
          </Text>
          <XStack gap="$spacing-inset-quarck" alignItems="center">
            {renderLessonStatus}
          </XStack>
        </YStack>
      </XStack>
    </View>
  )
}
